<template>
    <div>
        <psi-page-title
            :title="page.title"
            :icon="page.icon"
            :breadcrumbs="breadcrumbs"
        ></psi-page-title>
        <order-test-toolbar></order-test-toolbar>
        <!-- Content always starts with a v-card  -->
        <v-card class="mt-4 pa-3">
            <psi-toolbar
                :filters="filters"
                :quicklinks="quicklinks"
                :actions="$config('screen.actions')"
                :showActionMenu="selectedItems.length > 0"
                :view-option.sync="viewOption"
                @filter-item="filterItem"
                @filter-clear-all="filterClearAll"
                @quicklink-action="quicklinkAction"
                @action="toolbarAction"
                @refresh="refresh"
                close-on-select
                close-on-clear
            ></psi-toolbar>
            <screening-history-paginator
                v-if="Object.keys(pagination).length > 0 && screens.length > 0"
                :per_page.sync="per_page"
                :current_page.sync="current_page"
                :total_pages="pagination.last_page"
                v-bind="pagination"
            >
            </screening-history-paginator>
            <keep-alive>
                <component
                    :loading="loading"
                    :screens="screens"
                    :screeners="screeners"
                    :is="`screening-${viewOption}`"
                    @item-selected="itemSelected"
                    @toggle-select-all="toggleSelectAll"
                    @action="orderAction"
                ></component>
            </keep-alive>
        </v-card>
    </div>
</template>
<script>
import { mapActions, mapGetters } from "vuex";
import ScreeningList from "@components/screen/components/ScreeningList";
import ScreeningGrid from "@components/screen/components/ScreeningGrid";
import OrderTestToolbar from "@components/screen/components/OrderTestToolbar";
import ScreeningHistoryPaginator from "@components/screen/components/ScreeningHistoryPaginator";
import HomeLink from "@/mixins/homelink";

export default {
    name: "screening-history",
    components: {
        ScreeningList,
        ScreeningGrid,
        OrderTestToolbar,
        ScreeningHistoryPaginator,
    },
    mixins: [HomeLink],
    data() {
        return {
            page: {
                title: "Order History",
                icon: "mdi-account-clock",
            },

            filters: this.$config("screen.filters"),
            quicklinks: this.$config("screen.quicklinks"),
            selectedItems: [],
            viewOption: this.$vuetify.breakpoint.mobile ? "grid" : "list",
            per_page: this.$config("screen.pagination.perPage"),
            current_page: 1, // start on page 1
        };
    },
    computed: {
        ...mapGetters("Screens", [
            "screens",
            "screenFilters",
            "pagination",
            "currentScreen",
            "screeners",
            "loading",
        ]),
        breadcrumbs() {
            return [
                Object.assign(
                    {
                        text: "Home",
                        disabled: false,
                    },
                    this.homeUrl
                ),
                {
                    text: "Order History",
                    disabled: true,
                    to: "#",
                },
            ];
        },
    },
    watch: {
        current_page() {
            this.refresh();
        },
        per_page() {
            // start at page 1 when changing per page counts
            this.current_page = 1;
            this.refresh();
        },
        screenFilters: {
            immediate: true,
            handler(filter) {
                if (filter && Object.keys(filter).length) {
                    this.updateFilter(
                        "company",
                        this.$_.get(filter, "companies", [])
                    );
                    this.updateFilter(
                        "property",
                        this.$_.get(filter, "properties", [])
                    );
                    this.updateFilter(
                        "assigned_to",
                        this.$_.get(filter, "screeners", [])
                    );
                }
            },
        },
    },
    methods: {
        ...mapActions("Screens", [
            "getScreens",
            "getCurrentScreen",
            "getScreeners",
            "deleteMultiple",
            "resubmitScreen",
        ]),
        refresh() {
            // Derive filters and issue filter orders call
            this.filterOrders();
        },
        toolbarAction(event) {
            let order_ids = [];
            switch (event.slug) {
                case "delete-orders":
                    order_ids = this.selectedItems.map((item) => item.order_id);
                    this.deleteMultiple(order_ids);
                    this.selectedItems = [];
                    break;
            }
        },
        quicklinkAction(event) {
            switch (event.title) {
                // TODO: change from title based to name based
                case "Pending Orders":
                    // Clear out any set filters
                    this.resetFilters();
                    // Add only this filter from the quicklink
                    this.updateFilter("status", "Pending", "value");
                    // Get the result data
                    this.filterOrders();
                    break;
            }
        },

        itemSelected({ item, value }) {
            if (value) {
                // selected an item
                this.selectedItems.push(item);
            } else {
                // removed an item
                this.selectedItems.splice(
                    this.selectedItems.findIndex(
                        (elem) => elem.order_id === item.order_id
                    ),
                    1
                );
            }
        },
        toggleSelectAll({ items, value }) {
            if (value) {
                this.selectedItems = items;
            } else {
                this.selectedItems = [];
            }
        },
        orderAction(order, item) {
            switch (item.name) {
                case "view-order":
                    this.$router.push({
                        name: "Application Process",
                        params: { id: order.order_id },
                    });
                    break;
                case "resubmit-order":
                    this.resubmitScreen(order.applicant.applicant_id);
                    this.$toast({
                        content: "Order Resubmitted",
                    });
                    break;
            }
        },

        /**
         * Run the result from the filter
         */
        filterOrders() {
            this.getScreens({
                filters: this.getFilter(),
                pagination: this.getPagination(),
            });
        },
        // Reset Filters to default values
        resetFilters() {
            this.filters.forEach((item) => {
                this.updateFilter(item.name, item.default, "value");
            });
        },
        // update a filter item value on the filters data
        updateFilter(filterName, data, key = "items") {
            const index = this.filters.findIndex(
                (item) => item.name == filterName
            );
            if (index !== -1) {
                let filter = this.filters[index];
                filter[key] = data;
                this.filters.splice(index, 1, filter);
            }
        },
        // set a given item to a filter value
        filterItem(item) {
            this.updateFilter(item.name, item.value, "value");
            this.filterOrders();
        },
        filterClearAll() {
            this.resetFilters();
            this.filterOrders();
        },
        // get a filter structure for the given filters with values
        getFilter() {
            let filter = {};
            this.filters.forEach((item) => {
                if (this.$_.get(item, "value", "").toString().length) {
                    filter[item.name] = {
                        value: this.$_.get(item, "value", ""),
                    };
                }
            });
            return filter;
        },
        getPagination() {
            return {
                current_page: this.current_page,
                per_page: this.per_page,
            };
        },
    },
    mounted() {
        this.filterOrders();
        this.getScreeners();
    },
    created() {},
};
</script>

<style scoped>
</style>